import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { UpsertAllowanceQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { Panes } from 'modules/core/components/Panes'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'
import { usePrompt } from 'modules/core/lib/react-router'

import { canAccessWelcomeSpace } from 'modules/employees/lib/utils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import AllowanceForm from 'modules/timeoff/lib/AllowanceForm'
import allowances from 'modules/timeoff/store/collections/allowances'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'

import AllowanceSettings from './AllowanceSettings'
import BasicInformation from './BasicInformation'
import CarryOverSettings from './CarryOverSettings'
import CycleSettings from './CycleSettings'
import SaveModal from './SaveModal'
import TenurePeriods from './TenurePeriods'

interface Props {
  policyId: number
  allowanceId?: number
  allowanceName?: string
  loadingQuery: boolean
  queryData?: UpsertAllowanceQuery
}

const UpsertAllowancePage = ({
  policyId,
  allowanceId,
  allowanceName,
  loadingQuery,
  queryData,
}: Props) => {
  const [showConfirmSaveModal, setShowConfirmSaveModal] = React.useState<boolean>(false)

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const backToPolicyDetails = () => {
    navigate(navigation.settings.timeoffPolicies.id.details.fullPath({ id: policyId }))
  }

  const [allowance] = React.useState<Allowance>((): Allowance => {
    return new Allowance({
      id: allowanceId,
      timeoff_policy_id: policyId,
      source_units: Allowance.SOURCE_UNITS.base_units,
      leave_type_ids: [],
    })
  })

  const { loading: loadingLeaveTypes } = useUnsafeAsyncData(() => {
    let promises = [
      leaveTypes.fetch({
        data: {
          active: true,
        },
      }),
      allowances.fetch({
        data: {
          timeoff_policy_id: policyId,
        },
      }),
    ]

    if (!allowance.isNew) {
      promises = [...promises, allowance.fetch()]
    }

    return Promise.all(promises)
  }, [allowance])

  const { employee, company } = useCurrentSession()

  const featureAvailabilities = queryData?.companies.featureAvailabilities ?? []
  // TODO: Remove this once the FF 'timeoff_count_rest_days_option' is removed and this option is available for everyone
  const features = queryData?.features.featuresConnection.nodes ?? []
  const countSmRestDaysOptionAvailable = !!features.find(
    (f) => f.id === `${company.id}-dev_timeoff_count_rest_days_option` && f.enabled === true
  )
  // END TODO

  const flexibleCyclesEnabled = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_flexible_cycles
  )

  const hasOvertimeTimeOff = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_overtime_compensation
  )

  const hasProratedTenures = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.timeoff_prorated_tenure_periods
  )

  const loading = loadingLeaveTypes || loadingQuery

  const allowanceForm = useUnsafeMemo(() => {
    const allowanceParams =
      !allowance.isNew && !loading
        ? allowance.toJS()
        : {
            timeoff_policy_id: policyId,
            name: allowanceName,
          }

    return new AllowanceForm(allowanceParams, flexibleCyclesEnabled)
  }, [allowance, loading, policyId, allowanceName, flexibleCyclesEnabled])

  usePrompt(
    i18n.t('common.confirm_navigation'),
    () => allowanceForm.isDirty && !allowanceForm.saved
  )

  const timeTrackingEnabled = queryData?.companies.moduleAvailabilities[0]?.available
  const canSeeOthersAttendance = !!queryData?.attendance._policies.companySeeOthers.can
  const hasAccruedPaidTimeOff = (() => {
    if (!timeTrackingEnabled) return false
    if (employee && canAccessWelcomeSpace(employee)) return false

    return canSeeOthersAttendance
  })()

  const isHourly = allowanceForm.isHourly
  const isTimeWorked =
    hasAccruedPaidTimeOff && (allowanceForm.isTimeWorked || allowance.isTimeWorked)
  const isOvertime = hasOvertimeTimeOff && (allowanceForm.isOvertime || allowance.isOvertime)

  const disableSubmit =
    !allowanceForm.get('name').value ||
    !allowanceForm.get('leave_type_ids').value?.length ||
    !allowanceForm.isDirty

  const extendSettings = !allowanceForm.get('unlimited_holidays').value && !isOvertime

  const { running: submitting, performAction: onConfirm } = useAsyncAction(async () => {
    allowanceForm.saved = true

    allowance.isNew ? await allowanceForm.create(allowances) : await allowanceForm.save(allowance)

    setShowConfirmSaveModal(false)
    backToPolicyDetails()
  })

  return (
    <ResponsivePage.Body
      loading={loading}
      error={null}
      showHeader={false}
      transactionalActions={{
        primary: {
          label: i18n.t('employees.save_changes'),
          onClick: () => setShowConfirmSaveModal(true),
          disabled: disableSubmit,
        },
        secondary: {
          label: i18n.t('common.cancel'),
          onClick: backToPolicyDetails,
        },
      }}
    >
      <Panes direction="vertical">
        <BasicInformation
          allowance={allowance}
          allowanceForm={allowanceForm}
          isOvertime={isOvertime}
          hasOvertimeTimeOff={hasOvertimeTimeOff}
          hasAccruedPaidTimeOff={hasAccruedPaidTimeOff}
        />

        {flexibleCyclesEnabled && !isOvertime && (
          <CycleSettings allowance={allowance} allowanceForm={allowanceForm} />
        )}

        <AllowanceSettings
          allowance={allowance}
          allowanceForm={allowanceForm}
          isHourly={isHourly}
          featureAvailabilities={featureAvailabilities}
          isTimeWorked={isTimeWorked}
          isOvertime={isOvertime}
          // TODO: Remove countSmRestDaysOptionAvailable once Feature Flag is not needed
          countSmRestDaysOptionAvailable={countSmRestDaysOptionAvailable}
        />

        {extendSettings && (
          <Panes direction="vertical">
            <CarryOverSettings allowanceForm={allowanceForm} isHourly={isHourly} />
            <TenurePeriods
              allowanceForm={allowanceForm}
              isHourly={isHourly}
              hasProratedTenures={hasProratedTenures}
            />
          </Panes>
        )}
      </Panes>
      {showConfirmSaveModal && (
        <SaveModal
          allowance={allowance}
          allowanceForm={allowanceForm}
          closeModal={() => setShowConfirmSaveModal(false)}
          onConfirm={onConfirm}
          submitting={submitting}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default observer(UpsertAllowancePage)
